import React, { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useFilterStoreWithDayjsDates } from "../store/filterStore";
import { dimensionById, TOTAL_DIMENSION } from "../views/cost-analyzer/analyzerDimensions";

// PageContext gives the "Ask InstantView" assistant a best-effort hint of what
// the user is currently looking at — route, the analyzer's active period, and
// a compact scope string (e.g. "service=BigQuery") — so a question like "why
// did this change?" can resolve against the current screen instead of forcing
// a clarifying round-trip. It is a HINT only: the backend (parse.go) never
// lets it bypass BuildSeed's validation.
//
// The default context value (no provider) matches the degraded case below —
// route only, period/scope undefined — so consumers (TopBar) and their tests
// work identically whether or not a PageContextProvider is mounted above them.
const PageContext = createContext({ route: undefined, period: undefined, scope: undefined });

export function usePageContext() {
  return useContext(PageContext);
}

// buildScopeHint — pure derivation of a compact human-readable scope string
// from the analyzer's current dimension/filters/drill-path. Deliberately
// simple (a hint, not an exhaustive description): flat dimensions narrowed to
// one value read as "field=value"; a perspective drilled to an owner node
// reads as "dimensionCode=label"; anything else falls back to just the
// dimension's label, and Total yields no scope at all.
function buildScopeHint({ dimension, filters, drillPath }) {
  if (!dimension || dimension.id === "total") return undefined;
  if (dimension.kind === "perspective") {
    const lastNodeCrumb = [...(drillPath || [])].reverse().find((c) => c && c.label);
    return lastNodeCrumb ? `${dimension.dimensionCode}=${lastNodeCrumb.label}` : dimension.label;
  }
  if (dimension.groupingField) {
    const match = (filters || []).find((f) => f && f.category === dimension.groupingField);
    const value = match?.selectedValues?.[0];
    if (value) return `${dimension.groupingField}=${value}`;
  }
  return dimension.label;
}

// Reads period/scope straight from the filterStore (rather than the richer
// useAnalyzerScope hook, which builds request payloads this provider doesn't
// need). The store hook itself (useFilterStoreWithDayjsDates) is a plain
// Zustand subscription with full defaults (see store/filterStore.js) — it
// does not throw, and calling it must stay unconditional (react-hooks/
// rules-of-hooks forbids a hook call inside try/catch). What's genuinely
// unpredictable is the SHAPE of what comes back (analyzerDimension missing,
// startDate not hydrated yet, a future store refactor, ...), so that
// derivation is guarded: any failure there degrades to {} — period/scope
// simply omitted — never throws, never crashes the app shell that renders on
// every route.
function usePeriodAndScope() {
  const store = useFilterStoreWithDayjsDates();

  return useMemo(() => {
    try {
      const { startDate, analyzerDimension, analyzerDrillPath, filters } = store || {};
      if (!startDate || typeof startDate.format !== "function") return {};
      const dimension = dimensionById(analyzerDimension?.id) || TOTAL_DIMENSION;
      return {
        period: startDate.format("YYYY-MM"),
        scope: buildScopeHint({ dimension, filters, drillPath: analyzerDrillPath }),
      };
      // eslint-disable-next-line no-unused-vars
    } catch (_err) {
      return {};
    }
  }, [store]);
}

export function PageContextProvider({ children }) {
  const location = useLocation();
  const { period, scope } = usePeriodAndScope();

  const value = useMemo(
    () => ({ route: location.pathname, period, scope }),
    [location.pathname, period, scope]
  );

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}
